'use client';

import { useEffect, useState } from 'react';

interface Section {
  id: string;
  label: string;
}

interface SectionNavProps {
  sections: Section[];
  sentinelId?: string;
}

export default function SectionNav({ sections, sentinelId = 'section-nav-anchor' }: SectionNavProps) {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const NAV_OFFSET = 96; // px to account for sticky bar height

  useEffect(() => {
    const sentinel = document.getElementById(sentinelId);
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setShow(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const onScroll = () => setExpanded(false);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [expanded]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const visibleSections = expanded || !isMobile ? sections : sections.slice(0, 3);

  return (
    <div
      className={`pointer-events-none fixed inset-x-0 top-0 z-30 transition-opacity duration-200 ${
        show ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="mx-auto max-w-6xl px-4 py-3">
        <div className="pointer-events-auto flex items-center gap-2 rounded-2xl bg-[hsl(var(--ghost))]/90 backdrop-blur shadow-lg border border-border px-2 py-1">
          <div className={`flex gap-2 ${expanded ? 'flex-wrap' : 'flex-nowrap'}`}>
            {visibleSections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollTo(section.id)}
                className="px-3 py-2 text-sm font-semibold text-[hsl(var(--ghost-foreground))] hover:text-primary transition-colors"
              >
                {section.label}
              </button>
            ))}
          </div>
          {!expanded && isMobile && sections.length > visibleSections.length && (
            <button
              onClick={() => setExpanded(true)}
              className="ml-auto px-3 py-2 text-sm font-semibold text-[hsl(var(--ghost-foreground))] sm:hidden"
              aria-label="Show all sections"
            >
              <span className="inline-flex items-center gap-1" aria-hidden="true">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
